import { FC } from "react";
import {
  Container,
  Card,
  CardContent,
  CardHeader,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import useNotificationControlModel from "./useNotificationControlModel";
import StrategySelector from "./components/StrategySelector";
import ChannelControls from "./components/ChannelControls";
import TimePicker from "./components/TimePicker";
import FormActions from "./components/FormActions";
import { CreditCard, Medication } from "@mui/icons-material";

const NotificationControlView: FC<ReturnType<
  typeof useNotificationControlModel
>> = ({
  preferences,
  isLoading,
  isSaving,
  error,
  successMessage,
  hasChanges,
  capsuleStrategies,
  paymentStrategies,
  isAdmin,
  userDetails,
  updatePreference,
  handleSave,
  handleDiscard,
}) => {
  if (isLoading && !preferences) {
    return (
      <Container className="my-5">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container className="my-5">
      {preferences && (
        <Card elevation={3}>
          <CardHeader
            title={
              <Box>
                <Typography variant="h5">
                  Preferências de Notificação
                </Typography>
                {isAdmin && (
                  <Typography variant="subtitle2" color="textSecondary">
                    Editando preferências de:{" "}
                    <strong>{userDetails?.name}</strong>
                  </Typography>
                )}
              </Box>
            }
          />

          <CardContent>
            {/* Mensagens de Feedback */}
            {error && (
              <Alert
                severity="error"
                onClose={() => {
                  "";
                }}
                className="mb-3"
              >
                {error}
              </Alert>
            )}

            {successMessage && (
              <Alert
                severity="success"
                onClose={() => {
                  "";
                }}
                className="mb-3"
              >
                {successMessage}
              </Alert>
            )}

            {/* Seção de Estratégias de Notificação */}
            <Box className="mb-4">
              <Typography variant="h6" className="mb-3">
                Estratégias de Notificação
              </Typography>

              <StrategySelector
                label="Cápsulas"
                icon={<Medication />}
                value={preferences.capsuleNotificationStrategy}
                options={capsuleStrategies}
                description={preferences.capsuleStrategyDescription}
                onChange={value =>
                  updatePreference("capsuleNotificationStrategy", value)
                }
                disabled={isSaving}
              />

              <StrategySelector
                label="Pagamentos"
                icon={<CreditCard />}
                value={preferences.paymentNotificationStrategy}
                options={paymentStrategies}
                description={preferences.paymentStrategyDescription}
                onChange={value =>
                  updatePreference("paymentNotificationStrategy", value)
                }
                disabled={isSaving}
                className="mt-3"
              />
            </Box>

            <Divider className="my-4" />

            {/* Seção de Canais de Comunicação */}
            <Box className="mb-4">
              <Typography variant="h6" className="mb-3">
                Canais de Comunicação
              </Typography>

              <ChannelControls
                emailEnabled={preferences.receiveEmailNotifications}
                pushEnabled={preferences.receivePushNotifications}
                onEmailChange={value =>
                  updatePreference("receiveEmailNotifications", value)
                }
                onPushChange={value =>
                  updatePreference("receivePushNotifications", value)
                }
                disabled={isSaving}
              />
            </Box>

            <Divider className="my-4" />

            {/* Seção de Horário Preferido */}
            <Box className="mb-4">
              <Typography variant="h6" className="mb-3">
                Horário Preferido
              </Typography>

              <TimePicker
                value={preferences.preferredNotificationTime || "12:00"}
                onChange={value =>
                  updatePreference("preferredNotificationTime", value)
                }
                disabled={isSaving}
              />
            </Box>

            <Divider className="my-4" />

            {/* Botões de Ação */}
            <FormActions
              onSave={handleSave}
              onDiscard={handleDiscard}
              isLoading={isSaving}
              hasChanges={hasChanges}
            />
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default NotificationControlView;
